import * as React from 'react'

/**
 * A step state that takes the frame over (e.g. an error screen shown in place
 * of the step): the stepper and the footer are left out and the back control
 * calls `onStepBack` instead of leaving the step.
 */
export interface StepTakeover {
  onStepBack?: () => void
}

/**
 * Bridges dynamic footer state from a driver step (which owns the state) up to
 * the sticky `StepNavigationFooter` (rendered by `StepLayout`, a sibling of the
 * step). The "Next disabled" flag is bridged — steps whose primary
 * action must stay gated (e.g. service-location until a supplier is chosen,
 * summary while the booking request is in flight) call `useSetStepFooterDisabled`.
 * Steps that don't need it render nothing and Next stays enabled (form
 * validation gates advancement). A step can also take the frame over, see
 * `StepTakeover`.
 */
interface StepFooterContextValue {
  nextDisabled: boolean
  setNextDisabled: (disabled: boolean) => void
  takeover?: StepTakeover
  setTakeover: (takeover?: StepTakeover) => void
}

const StepFooterContext = React.createContext<StepFooterContextValue>({
  nextDisabled: false,
  setNextDisabled: () => undefined,
  setTakeover: () => undefined,
})

export const StepFooterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [nextDisabled, setNextDisabled] = React.useState(false)
  const [takeover, setTakeover] = React.useState<StepTakeover>()

  return (
    <StepFooterContext.Provider
      value={{ nextDisabled, setNextDisabled, takeover, setTakeover }}
    >
      {children}
    </StepFooterContext.Provider>
  )
}

/** Read the current "Next disabled" state (used by the footer). */
export const useStepFooterNextDisabled = (): boolean =>
  React.useContext(StepFooterContext).nextDisabled

/** Push a "Next disabled" state up to the footer for the lifetime of a step. */
export const useSetStepFooterDisabled = (disabled: boolean): void => {
  const { setNextDisabled } = React.useContext(StepFooterContext)

  React.useEffect(() => {
    setNextDisabled(disabled)

    return () => setNextDisabled(false)
  }, [disabled, setNextDisabled])
}

/**
 * Renders nothing; bridges a step's "Next disabled" state to the footer. Use
 * this inside render-prop children (e.g. react-final-form) where hooks cannot
 * be called directly.
 */
export const StepFooterDisabledSync: React.FC<{ disabled: boolean }> = ({
  disabled,
}) => {
  useSetStepFooterDisabled(disabled)

  return null
}

/** Read the current takeover (used by the layout). */
export const useStepTakeover = (): StepTakeover | undefined =>
  React.useContext(StepFooterContext).takeover

/** Take the frame over while `active`; `onStepBack` should be stable. */
export const useSetStepTakeover = (
  active: boolean,
  onStepBack?: () => void,
): void => {
  const { setTakeover } = React.useContext(StepFooterContext)

  React.useEffect(() => {
    setTakeover(active ? { onStepBack } : undefined)

    return () => setTakeover(undefined)
  }, [active, onStepBack, setTakeover])
}
